import React from 'react'
import BeamDiagramKonva from '../../components/BeamDiagramKonva'
import { BeamKonvaVisualization } from '../../components/BeamVisualizationKit'
import CodeCheckDocTable from '../../components/CodeCheckDocTable'
import { SMBeamModel } from '../../types'
import { ResponseDiagramType } from './types'
import { useTranslation, TranslationKey } from '@structura/i18n'

interface ResponseVisualizationPanelProps
{
  model: SMBeamModel
  currentMaterial: string
  selectedDiagram: ResponseDiagramType
  diagramScale: number
  showValues: boolean
  showMaxMin: boolean
  valueDecimals: 2 | 4
  valueDensity: number
  valueFontSize?: number
  diagramData: any
  diagramAvailable: boolean
  // Label of the combination the diagram was actually evaluated for, set only when it differs
  // from the user's dropdown selection (each SLS check lives on its code-mandated combination).
  diagramSourceLabel: string
  // True when NO combination evaluated the check behind the selected diagram (e.g. an opt-in
  // limit that was never configured) — the chart would only show a meaningless zero curve.
  diagramNotEvaluated: boolean
  globalChecks: unknown[] | undefined
}

function resolveDiagramIcon(diagram: ResponseDiagramType): string
{
  switch (diagram)
  {
    case 'epsilonTop':
    case 'epsilonMid':
    case 'epsilonBot':
      return 'bi-diagram-3'
    case 'sigmaTop':
    case 'sigmaMid':
    case 'sigmaBot':
      return 'bi-diagram-3-fill'
    case 'gammaLower':
    case 'gammaMid':
    case 'gammaUpper':
      return 'bi-diagram-2'
    case 'tauLower':
    case 'tauMid':
    case 'tauUpper':
      return 'bi-diagram-2-fill'
    case 'urMaxOverall':
    case 'urBending':
    case 'urCompression':
    case 'urTension':
    case 'urShear':
    case 'urSlsDeflection':
    case 'urSlsCrackWidth':
    case 'urSlsConcreteStress':
    case 'urSlsSteelStress':
      return 'bi-lightning-fill'
    case 'slsDeflectionInst':
    case 'slsDeflectionFinal':
      return 'bi-graph-down'
    default:
      return 'bi-graph-up'
  }
}

function resolveDiagramDisplayNameKey(diagram: ResponseDiagramType, material: string): TranslationKey
{
  // Steel does not creep: its w_fin equals w_inst, and the heading must not claim "incl. creep".
  if (diagram === 'slsDeflectionFinal' && material.toLowerCase() === 'steel')
  {
    return 'beam.response.nameSlsDeflectionFinalSteel'
  }
  const nameMap: Record<ResponseDiagramType, TranslationKey> = {
    epsilonTop: 'beam.response.nameEpsilonTop',
    epsilonMid: 'beam.response.nameEpsilonMid',
    epsilonBot: 'beam.response.nameEpsilonBot',
    sigmaTop: 'beam.response.nameSigmaTop',
    sigmaMid: 'beam.response.nameSigmaMid',
    sigmaBot: 'beam.response.nameSigmaBot',
    gammaLower: 'beam.response.nameGammaLower',
    gammaMid: 'beam.response.nameGammaMid',
    gammaUpper: 'beam.response.nameGammaUpper',
    tauLower: 'beam.response.nameTauLower',
    tauMid: 'beam.response.nameTauMid',
    tauUpper: 'beam.response.nameTauUpper',
    urMaxOverall: 'beam.response.nameUrMaxOverall',
    urBending: 'beam.response.nameUrBending',
    urCompression: 'beam.response.nameUrCompression',
    urTension: 'beam.response.nameUrTension',
    urShear: 'beam.response.nameUrShear',
    urSlsDeflection: 'beam.response.nameUrSlsDeflection',
    urSlsCrackWidth: 'beam.response.nameUrSlsCrackWidth',
    urSlsConcreteStress: 'beam.response.nameUrSlsConcreteStress',
    urSlsSteelStress: 'beam.response.nameUrSlsSteelStress',
    slsDeflectionInst: 'beam.response.nameSlsDeflectionInst',
    slsDeflectionFinal: 'beam.response.nameSlsDeflectionFinal'
  }
  return nameMap[diagram] || 'beam.response.nameDefault'
}

function resolveDiagramStyle(diagram: ResponseDiagramType): { label?: string; color?: string; fill?: string; scaleMultiplier?: number }
{
  const configuration: Record<ResponseDiagramType, { label: string; color: string; fill: string; scale: number }> = {
    epsilonTop: { label: 'ε Top (‰)', color: '#0d6efd', fill: 'rgba(13,110,253,0.2)', scale: 1000 },
    epsilonMid: { label: 'ε Mid (‰)', color: '#0d6efd', fill: 'rgba(13,110,253,0.2)', scale: 1000 },
    epsilonBot: { label: 'ε Bot (‰)', color: '#0d6efd', fill: 'rgba(13,110,253,0.2)', scale: 1000 },
    sigmaTop: { label: 'σ Top (MPa)', color: '#dc3545', fill: 'rgba(220,53,69,0.2)', scale: 1e-6 },
    sigmaMid: { label: 'σ Mid (MPa)', color: '#dc3545', fill: 'rgba(220,53,69,0.2)', scale: 1e-6 },
    sigmaBot: { label: 'σ Bot (MPa)', color: '#dc3545', fill: 'rgba(220,53,69,0.2)', scale: 1e-6 },
    gammaLower: { label: 'γ Lower (‰)', color: '#20c997', fill: 'rgba(32,201,151,0.2)', scale: 1000 },
    gammaMid: { label: 'γ Mid (‰)', color: '#20c997', fill: 'rgba(32,201,151,0.2)', scale: 1000 },
    gammaUpper: { label: 'γ Upper (‰)', color: '#20c997', fill: 'rgba(32,201,151,0.2)', scale: 1000 },
    tauLower: { label: 'τ Lower (MPa)', color: '#6f42c1', fill: 'rgba(111,66,193,0.2)', scale: 1e-6 },
    tauMid: { label: 'τ Mid (MPa)', color: '#6f42c1', fill: 'rgba(111,66,193,0.2)', scale: 1e-6 },
    tauUpper: { label: 'τ Upper (MPa)', color: '#6f42c1', fill: 'rgba(111,66,193,0.2)', scale: 1e-6 },
    urMaxOverall: { label: 'Max Overall UR', color: '#fd7e14', fill: 'rgba(253,126,20,0.2)', scale: 1 },
    urBending: { label: 'Bending UR', color: '#fd7e14', fill: 'rgba(253,126,20,0.2)', scale: 1 },
    urCompression: { label: 'Compression UR', color: '#fd7e14', fill: 'rgba(253,126,20,0.2)', scale: 1 },
    urTension: { label: 'Tension UR', color: '#fd7e14', fill: 'rgba(253,126,20,0.2)', scale: 1 },
    urShear: { label: 'Shear UR', color: '#fd7e14', fill: 'rgba(253,126,20,0.2)', scale: 1 },
    urSlsDeflection: { label: 'SLS Deflection UR', color: '#0dcaf0', fill: 'rgba(13,202,240,0.2)', scale: 1 },
    urSlsCrackWidth: { label: 'SLS Crack Width UR', color: '#0dcaf0', fill: 'rgba(13,202,240,0.2)', scale: 1 },
    urSlsConcreteStress: { label: 'SLS Concrete Stress UR', color: '#0dcaf0', fill: 'rgba(13,202,240,0.2)', scale: 1 },
    urSlsSteelStress: { label: 'SLS Steel Stress UR', color: '#0dcaf0', fill: 'rgba(13,202,240,0.2)', scale: 1 },
    slsDeflectionInst: { label: 'w_inst (mm)', color: '#198754', fill: 'rgba(25,135,84,0.2)', scale: 1 },
    slsDeflectionFinal: { label: 'w_fin (mm)', color: '#198754', fill: 'rgba(25,135,84,0.2)', scale: 1 }
  }
  const style = configuration[diagram]
  if (!style)
  {
    return {}
  }
  return { label: style.label, color: style.color, fill: style.fill, scaleMultiplier: style.scale }
}

const ResponseVisualizationPanel: React.FC<ResponseVisualizationPanelProps> = ({
  model,
  currentMaterial,
  selectedDiagram,
  diagramScale,
  showValues,
  showMaxMin,
  valueDecimals,
  valueDensity,
  valueFontSize,
  diagramData,
  diagramAvailable,
  diagramSourceLabel,
  diagramNotEvaluated,
  globalChecks
}) =>
{
  const { t } = useTranslation()
  const diagramStyle = resolveDiagramStyle(selectedDiagram)
  const displayName = t(resolveDiagramDisplayNameKey(selectedDiagram, currentMaterial))
  const icon = resolveDiagramIcon(selectedDiagram)
  const isUrDiagram = selectedDiagram.startsWith('ur')

  return (
    <div>
      <div className="card mb-3">
        <div className="card-header beam-card-header">
          <h5 className="card-title mb-0">
            <i className={`${icon} me-2`}></i>
            {displayName}
          </h5>
        </div>
        <div className="card-body">
          {diagramNotEvaluated && (
            <div className="alert alert-info py-2 small mb-2">
              <i className="bi bi-info-circle me-1"></i>
              {t('beam.response.checkNotEvaluated')}
            </div>
          )}
          {!diagramNotEvaluated && diagramSourceLabel && (
            <div className="text-muted small mb-2">
              <i className="bi bi-arrow-return-right me-1"></i>
              {t('beam.response.evaluatedFor', { combination: diagramSourceLabel })}
            </div>
          )}
          {/* A not-evaluated check has no meaningful curve: the notice replaces the chart and
              the plain beam visualization is shown instead of a zero-filled diagram. */}
          {diagramAvailable && !diagramNotEvaluated ? (
            <BeamDiagramKonva
              key={`response-diagram-${selectedDiagram}`}
              model={model}
              diagramType={'none'}
              diagramData={diagramData}
              scale={diagramScale}
              showValues={showValues}
              showMaxMin={showMaxMin}
              valueDecimals={valueDecimals}
              valueDensity={valueDensity}
              valueFontSize={valueFontSize}
              labelOverride={diagramStyle.label}
              colorOverride={diagramStyle.color}
              fillColorOverride={diagramStyle.fill}
              scaleMultiplierOverride={diagramStyle.scaleMultiplier}
              {...(isUrDiagram ? { thresholdValue: 1, thresholdFillColor: 'rgba(220,53,69,0.35)' } : {})}
            />
          ) : (
            <BeamKonvaVisualization
              model={model}
              material={currentMaterial}
              storageKey="beamVisualization_response"
            />
          )}
        </div>
      </div>

      <CodeCheckDocTable checks={globalChecks} />
    </div>
  )
}

export default ResponseVisualizationPanel


